<template>
  <div>
    <div v-if="error" class="">
      <div class="alert alert-warning alert-dismissible">
        <i class="fas fa-exclamation-triangle text-danger" />
        <span class="font-weight-bold text-sm">{{ errorMessage }}</span>
        <button type="button" class="close" aria-label="Close" @click="() => (error = false)">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </div>
    <BushelTextInputMaterial
      type="text"
      name="companyName"
      autofocus
      :label-active="'Company Name'"
      :error="submitted && !formData.companyName"
      :value="formData.companyName"
      :error-text="'Required'"
      tabindex="1"
      @input="(companyName) => (formData.companyName = companyName)"
    />
    <BushelSelect
      class="pt-3 mb-3"
      :value="formData.subscriptionType"
      :options="subscriptionTypes"
      :error="submitted && !formData.subscriptionType"
      :searchable="false"
      :preselect-first="true"
      no-deselect
      no-padding
      label="text"
      label-active="Are you a..."
      @select="(subscriptionType) => (formData.subscriptionType = subscriptionType)"
    />
    <div class="d-flex">
      <BushelSelect
        class="w-100"
        :value="formData.companyCountry"
        :options="countries"
        :error="submitted && !formData.companyCountry"
        label="text"
        :loading="countriesLoading"
        no-deselect
        no-padding
        label-active="Country"
        @select="selectedCountry"
      />
      <BushelSelect
        class="w-100 ml-2"
        :value="formData.companyState"
        :options="states"
        :error="submitted && !formData.companyState"
        label="text"
        :loading="statesLoading"
        no-deselect
        no-padding
        label-active="State"
        @select="(companyState) => (formData.companyState = companyState)"
      />
    </div>
    <BushelTextInputMaterial
      type="text"
      name="name"
      :label-active="'Name'"
      :error="submitted && !formData.name"
      :value="formData.name"
      :error-text="'Required'"
      @input="(name) => (formData.name = name)"
    />
    <BushelTextInputMaterial
      type="email"
      name="email"
      :label-active="'Email'"
      :error="submitted && !validateEmail(formData.email)"
      autocomplete="email"
      :value="formData.email"
      :error-text="'Required'"
      @input="(email) => (formData.email = email)"
    />
    <BushelFlagMaterial
      :default-country="formData.country"
      name="registerPhone"
      type="tel"
      :thin="true"
      :error="submitted && !formData.phone"
      :label-active="'Phone'"
      :value="formData.phone"
      @country-selected="onSelectCountry"
      @input="(phone) => (formData.phone = phone)"
    />
    <div class="d-flex justify-space-between">
      <BushelTextInputMaterial
        :type="showPassword ? 'text' : 'password'"
        name="password"
        :error="submitted && formData.password.length < 8"
        :error-text="'8 char min, letters, mixedCase, numbers, symbols'"
        :label-active="'Password'"
        :value="formData.password"
        :message="'8 char min, letters, mixedCase, numbers, symbols'"
        @input="(password) => (formData.password = password)"
      />
      <BushelTextInputMaterial
        :type="showPassword ? 'text' : 'password'"
        class="ml-2"
        name="passwordConfirm"
        :error="submitted && formData.passwordConfirm.length < 8 && formData.passwordConfirm != formData.password"
        :error-text="'8 char min, letters, mixedCase, numbers, symbols'"
        :label-active="'Confirm'"
        :value="formData.passwordConfirm"
        :append-icon="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"
        :message="'8 char min, letters, mixedCase, numbers, symbols'"
        @icon-clicked="showPassword = !showPassword"
        @input="(passwordConfirm) => (formData.passwordConfirm = passwordConfirm)"
      />
    </div>
    <div>
      <p class="text-sm">8 char min, letters, mixedcase, numbers, and symbols</p>
    </div>
    <div class="d-flex align-items-center justify-content-center">
      <div class="custom-checkbox" :style="{ paddingLeft: 0, paddingRight: '0.25rem' }">
        <input
          id="acceptTerms"
          :value="formData.acceptTerms"
          name="acceptTerms"
          type="checkbox"
          :checked="formData.acceptTerms"
          @input="toggleCheckbox"
        />
        <label for="acceptTerms" class="cursor-pointer" :class="{ checkboxLabelError: submitted && !formData.acceptTerms }">
          <span class="checkbox-box" dusk="register-checkbox" />
          <div class="small">I Accept</div>
        </label>
      </div>
      <span class="text-success small text-nowrap clickable" @click="showTerms = !showTerms"> Terms And Conditions </span>
    </div>

    <BushelBtn class="btn-block mt-4" variant="success" @click="submit">
      Register
      <div v-if="submitting" class="spinner-border spinner-border-sm" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </BushelBtn>

    <BModal v-model="showTerms" max-width="500" scrollable size="lg" @hide="showTerms = false">
      <template #modal-title> Terms And Conditions </template>
      <Terms />
      <template #modal-footer>
        <BushelBtn variant="danger" ripple-color="danger" flat @click="disagree"> Disagree </BushelBtn>
        <BushelBtn variant="success" ripple-color="success" flat @click="agree"> Agree </BushelBtn>
      </template>
    </BModal>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import BushelTextInputMaterial from "@/components/common/forms/BushelTextInputMaterial.vue";
import BushelFlagMaterial from "@/components/common/forms/BushelFlagMaterial.vue";
import { validateEmail } from "@/services/helpers";
import * as api from "@/services/api";
import Terms from "@/components/common/Terms.vue";

export default {
  components: {
    BushelTextInputMaterial,
    BushelFlagMaterial,
    Terms,
  },
  props: {},
  emits: ["loggedIn"],
  data() {
    return {
      submitted: false,
      showPassword: false,
      error: false,
      errorMessage: "",
      countriesLoading: false,
      statesLoading: false,
      showTerms: false,
      countries: [],
      states: [],
      submitting: false,
      subscriptionTypes: [{ id: "buyer", text: "Buyer" }],
      formData: {
        companyName: "",
        subscriptionType: "",
        companyCountry: { id: 1, text: "United States", value: "US" },
        country: "US",
        countryCode: "+1",
        companyState: "",
        name: "",
        email: "",
        phone: "",
        password: "",
        passwordConfirm: "",
        acceptTerms: false,
      },
    };
  },
  mounted() {
    this.fetchAllCountries();
    this.fetchStates(this.formData.companyCountry.id);
  },
  methods: {
    ...mapActions({
      registerAction: "auth/register",
    }),
    validateEmail,
    toggleCheckbox() {
      if (this.formData.acceptTerms == true) {
        this.disagree();
      } else {
        this.agree();
      }
    },
    agree() {
      this.formData.acceptTerms = true;
      this.showTerms = false;
    },
    disagree() {
      this.formData.acceptTerms = false;
      this.showTerms = false;
    },
    onSelectCountry({ name, iso2, dialCode }) {
      this.formData.country = iso2;
      this.formData.countryCode = dialCode;
    },
    selectedCountry(country) {
      this.formData.companyCountry = country;
      this.fetchStates(country.id);
    },
    fetchAllCountries() {
      this.countriesLoading = true;
      api.get("countries").then((response) => {
        this.countriesLoading = false;
        this.countries = response.data;
      });
    },
    fetchStates(countryId) {
      this.statesLoading = true;
      return api.get(`states/by-country/${countryId}`).then((response) => {
        this.statesLoading = false;
        this.states = response.data;
      });
    },
    async submit() {
      this.submitted = true;
      this.submitting = true;
      if (
        this.validateEmail(this.formData.email) &&
        this.formData.phone &&
        this.formData.password.length >= 8 &&
        this.formData.passwordConfirm == this.formData.password &&
        this.formData.acceptTerms &&
        this.formData.name &&
        (this.formData.isInvite ||
          (this.formData.companyName && this.formData.subscriptionType && this.formData.companyCountry && this.formData.companyState))
      ) {
        const payload = {
          email: this.formData.email,
          name: this.formData.name,
          phone: this.formData.phone,
          phone_country: this.formData.country,
          country_code: this.formData.countryCode,
          company_country_id: this.formData.companyCountry?.id,
          company_state_id: this.formData.companyState?.id,
          subscription: this.formData.subscriptionType?.id,
          company_name: this.formData.companyName,
          invitation: this.formData.invitation,
          buyer_invitation: this.formData.buyerInvitation,
          brand_invitation: this.formData.brandInvitation,
          password: this.formData.password,
        };
        if (!this.formData.isInvite) {
          payload.subscription = this.formData.subscriptionType?.id;
          payload.company_name = this.formData.companyName;
          payload.company_country_id = this.formData.companyCountry?.id;
          payload.company_state_id = this.formData.companyState?.id;
          payload.form = "profile";
        }
        this.registerAction(payload).then((response) => {
          if (response == "success") {
            this.$emit("loggedIn", response.data);
          } else {
            this.error = true;
            if (response == "email taken") {
              this.errorMessage = "Sorry this email is already in use.";
            } else if (response && response.length && (typeof response === "string" || response instanceof String)) {
              this.errorMessage = response;
            } else {
              this.errorMessage = "Something went wrong. If you already have an account, please login or reset your password instead";
            }
            this.submitting = false;
          }
        });
      } else {
        this.error = true;
        this.errorMessage = "Something went wrong. If you already have an account, please login or reset your password instead";
        this.submitting = false;
      }
    },
    isValid() {
      return this.validateEmail(this.formData.email) && this.formData.password.length >= 8;
    },
  },
};
</script>
